import type * as PdfLibModule from '@cantoo/pdf-lib';

type PdfLib = typeof PdfLibModule;

/** Thrown only when the PDF itself cannot be split (password to open, damaged, no pages). */
export class PdfSplitError extends Error {}

export type PdfPage = { name: string; bytes: Uint8Array };

/** Above this many pages the operator confirms first: every page is one import and one Gemini call. */
export const SPLIT_CONFIRM_ABOVE_PAGES = 20;

type SplitOptions = { confirm?: (pageCount: number) => Promise<boolean> };

// Anything else (lazy chunk gone after a deploy, unreadable file) is fixed by a refresh, not another PDF.
export const splitFailureMessageId = (err: unknown): string =>
  err instanceof PdfSplitError ? 'waybills.imports.split.failed' : 'waybills.imports.split.unavailable';

const pageName = (fileName: string, page: number, total: number): string =>
  `${fileName.replace(/\.pdf$/i, '')} (p${page} of ${total}).pdf`;

const isLinkToPage = ({ PDFArray, PDFDict, PDFName, PDFRef }: PdfLib, annot: unknown): boolean => {
  if (!(annot instanceof PDFDict) || annot.get(PDFName.of('Subtype')) !== PDFName.of('Link')) return false;
  const pointsAtPage = (dest: unknown) => dest instanceof PDFArray && dest.get(0) instanceof PDFRef;
  const action = annot.lookup(PDFName.of('A'));
  return (
    pointsAtPage(annot.lookup(PDFName.of('Dest'))) ||
    (action instanceof PDFDict &&
      action.get(PDFName.of('S')) === PDFName.of('GoTo') &&
      pointsAtPage(action.lookup(PDFName.of('D'))))
  );
};

// copyPages follows every reference, so one to a page (widget /P, GoTo) drags that page in: cut them in the source.
const detachFromOtherPages = (lib: PdfLib, source: PdfLibModule.PDFDocument, index: number): void => {
  const { PDFArray, PDFDict, PDFName, PDFNull, PDFRef, PDFStream } = lib;
  const { context } = source;
  const { node } = source.getPage(index);

  const annots = node.lookup(PDFName.of('Annots'));
  if (annots instanceof PDFArray) {
    const kept = annots.asArray().filter((ref) => !isLinkToPage(lib, context.lookup(ref)));
    if (kept.length !== annots.size()) node.set(PDFName.of('Annots'), context.obj(kept));
  }

  const isPage = (value: unknown): boolean => {
    const target = value instanceof PDFRef ? context.lookup(value) : undefined;
    const type = target instanceof PDFDict ? target.get(PDFName.of('Type')) : undefined;
    return type === PDFName.of('Page') || type === PDFName.of('Pages');
  };
  const seen = new Set<PdfLibModule.PDFRef>();
  const cutPagesIn = (dict: PdfLibModule.PDFDict, keep?: PdfLibModule.PDFName): void => {
    for (const [key, entry] of dict.entries()) {
      if (key === keep) continue;
      if (isPage(entry)) dict.delete(key);
      else visit(entry);
    }
  };
  const visit = (value: unknown): void => {
    if (value instanceof PDFRef) {
      if (seen.has(value)) return;
      seen.add(value);
      visit(context.lookup(value));
    } else if (value instanceof PDFStream) {
      cutPagesIn(value.dict);
    } else if (value instanceof PDFDict) {
      cutPagesIn(value);
    } else if (value instanceof PDFArray) {
      value.asArray().forEach((entry, i) => {
        if (isPage(entry)) value.set(i, PDFNull);
        else visit(entry);
      });
    }
  };
  // The page's own /Parent stays: copyPages drops it and reads inherited attributes through it.
  cutPagesIn(node, PDFName.of('Parent'));
};

/** One PDF per page as `<base> (pN of M).pdf`; a one-page PDF comes back as is (dedups with a regular upload). */
export const splitPdfBytes = async (
  bytes: Uint8Array,
  fileName: string,
  { confirm }: SplitOptions = {},
): Promise<PdfPage[]> => {
  // Loaded on demand so the PDF library stays out of the main bundle.
  const lib = await import('@cantoo/pdf-lib');
  const { PDFDocument } = lib;
  try {
    // The empty password opens owner-password-only PDFs; ones that need a password to open still throw.
    const source = await PDFDocument.load(bytes, { password: '' });
    const total = source.getPageCount();
    if (total === 0) throw new PdfSplitError(`${fileName} has no pages`);
    if (total === 1) return [{ name: fileName, bytes }];
    // Declined → no pages; asked before the (costly) per-page copies.
    if (confirm && total > SPLIT_CONFIRM_ABOVE_PAGES && !(await confirm(total))) return [];

    const pages: PdfPage[] = [];
    for (let i = 0; i < total; i += 1) {
      try {
        detachFromOtherPages(lib, source, i);
      } catch {
        // Only trims the output: a structure it cannot walk is copied as copyPages takes it.
      }
      // No metadata: the same PDF must split into the same bytes, or sha256 dedup misses re-uploads.
      const doc = await PDFDocument.create({ updateMetadata: false });
      const [page] = await doc.copyPages(source, [i]);
      doc.addPage(page);
      pages.push({ name: pageName(fileName, i + 1, total), bytes: await doc.save() });
    }
    return pages;
  } catch (err) {
    if (err instanceof PdfSplitError) throw err;
    throw new PdfSplitError(`Could not split ${fileName}: ${(err as Error)?.message ?? err}`, { cause: err });
  }
};

export const splitPdfFile = async (file: File, options?: SplitOptions): Promise<File[]> => {
  const pages = await splitPdfBytes(new Uint8Array(await file.arrayBuffer()), file.name, options);
  if (pages.length === 1) return [file];
  // Explicit type: the backend stores the multipart type as the attachment's content type.
  return pages.map(
    ({ name, bytes }) => new File([bytes as Uint8Array<ArrayBuffer>], name, { type: 'application/pdf' }),
  );
};
